import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { EventNoteDto } from "@src/types/notes";

interface NotesState {
	notes: Array<EventNoteDto>;
	loading: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
}

const initialState: NotesState = {
	notes: [],
	loading: "idle",
	error: null,
};

interface CustomError {
	message: string;
	status?: number;
}

export const fetchNotesByEventId = createAsyncThunk("notes/fetchNotesByEventId", async (eventId: string, { rejectWithValue }) => {
	try {
		const marketplace = new URLSearchParams(window.location.search).get("marketplace") || "";
		const response = await axios.get(`${getBackendUrl()}/events/${eventId}/notes?marketplace=${marketplace}`, {
			headers: getAuthHeaders(),
		});
		return response.data;
	} catch (error) {
		return rejectWithValue("Failed to fetch notes");
	}
});

export const addNote = createAsyncThunk(
	"notes/addNote",
	async (
		{
			eventId,
			noteText,
		}: {
			eventId: string;
			noteText: string;
		},
		{ rejectWithValue },
	) => {
		try {
			const marketplace = new URLSearchParams(window.location.search).get("marketplace") || "";
			const response = await axios.post(
				`${getBackendUrl()}/events/${eventId}/notes?marketplace=${marketplace}`,
				{
					data: {
						text: noteText,
					},
				},
				{ headers: getAuthHeaders() },
			);
			return response.data;
		} catch (error) {
			// Handle different types of errors
			if (axios.isAxiosError(error)) {
				const axiosError = error as AxiosError;
				const customError: CustomError = {
					//@ts-expect-error - TS doesn't recognize that axiosError.response is defined
					message: axiosError.response?.data?.message || axiosError.message || "Failed to add note",
					status: axiosError.response?.status,
				};
				return rejectWithValue(customError);
			}
			return rejectWithValue({
				message: "An unexpected error occurred",
				status: 500,
			});
		}
	},
);

export const addBulkNotes = createAsyncThunk<Array<EventNoteDto>, { eventIds: Array<string>; noteText: string }>(
	"notes/addBulkNotes",
	async ({ eventIds, noteText }, { rejectWithValue }) => {
		try {
			const marketplace = new URLSearchParams(window.location.search).get("marketplace") || "";
			const { data } = await axios.post(
				`${getBackendUrl()}/events/notes/bulk?marketplace=${marketplace}`,
				{
					eventIds,
					text: noteText,
				},
				{ headers: getAuthHeaders() }
			);
			return data.notes
				? data.notes as Array<EventNoteDto>
				: (Array.isArray(data) ? data : []);
		} catch (err) {
			const message = axios.isAxiosError(err)
				? err.response?.data?.message || err.message
				: "An unexpected error occurred";
			return rejectWithValue({ message });
		}
	}
);

export const updateNote = createAsyncThunk(
	"notes/updateNote",
	async (
		{
			eventId,
			noteText,
			id,
		}: {
			eventId: string;
			noteText: string;
			id: number;
		},
		{ rejectWithValue },
	) => {
		try {
			const marketplace = new URLSearchParams(window.location.search).get("marketplace") || "";
			const response = await axios.put(
				`${getBackendUrl()}/events/${eventId}/notes?marketplace=${marketplace}`,
				{
					data: {
						text: noteText,
						id: id,
					},
				},
				{ headers: getAuthHeaders() },
			);
			return response.data;
		} catch (error) {
			// Handle different types of errors
			if (axios.isAxiosError(error)) {
				const axiosError = error as AxiosError;
				const customError: CustomError = {
					//@ts-expect-error - TS doesn't recognize that axiosError.response is defined
					message: axiosError.response?.data?.message || axiosError.message || "Failed to add note",
					status: axiosError.response?.status,
				};
				return rejectWithValue(customError);
			}
			return rejectWithValue({
				message: "An unexpected error occurred",
				status: 500,
			});
		}
	},
);

export const deleteNote = createAsyncThunk(
	"notes/deleteNote",
	async (
		{
			eventId,
			id,
		}: {
			eventId: string;
			id: number;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await axios.delete(`${getBackendUrl()}/events/${eventId}/notes/${id}`, { headers: getAuthHeaders() });
			return response.data;
		} catch (error) {
			// Handle different types of errors
			if (axios.isAxiosError(error)) {
				const axiosError = error as AxiosError;
				const customError: CustomError = {
					//@ts-expect-error - TS doesn't recognize that axiosError.response is defined
					message: axiosError.response?.data?.message || axiosError.message || "Failed to add note",
					status: axiosError.response?.status,
				};
				return rejectWithValue(customError);
			}
			return rejectWithValue({
				message: "An unexpected error occurred",
				status: 500,
			});
		}
	},
);

export const notesSlice = createSlice({
	name: "notes",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchNotesByEventId.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(fetchNotesByEventId.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.notes = Array.isArray(action.payload) ? [...action.payload].reverse() : [];
			})
			.addCase(fetchNotesByEventId.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(addNote.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(addNote.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.notes.push(action.payload);
			})
			.addCase(addNote.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(addBulkNotes.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(addBulkNotes.fulfilled, (state, action) => {
				state.loading = "succeeded";
				const notesToAdd = Array.isArray(action.payload) ? action.payload : [];
				state.notes.push(...notesToAdd);
			})
			.addCase(addBulkNotes.rejected, (state, action) => {
				state.loading = "failed";
				state.error = (action.payload as CustomError)?.message || "Failed to add bulk notes";
			})
			.addCase(updateNote.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(updateNote.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.notes = state.notes.map((note) => {
					if (note.id === action.payload.id) {
						return action.payload;
					}
					return note;
				});
			})
			.addCase(updateNote.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(deleteNote.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(deleteNote.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.notes = state.notes.filter((note) => note.id !== action.payload.id);
			})
			.addCase(deleteNote.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			});
	},
});

export default notesSlice.reducer;
